import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import _ from 'lodash';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import Switch from '@mui/material/Switch';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faXmark, faPenToSquare, faUserPlus, faTrash, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { faUsers } from '@fortawesome/pro-light-svg-icons';
import validator from 'email-validator';
import Header from '../Header';

// ── Pure, view-only helpers (no data mutation) ──
function initials(email) {
    const local = (email || '').split('@')[0] || '';
    const parts = local.split(/[._-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (local.slice(0, 2) || '?').toUpperCase();
}
function distinctTeamNames(users) {
    const seen = new Map();
    for (const u of users || []) {
        const name = ((u?.team || '').split(':')[0] || '').trim();
        if (!name) continue;
        const key = name.toLowerCase();
        if (!seen.has(key)) seen.set(key, name);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
}
function matchesQuery(user, query) {
    const q = (query || '').trim().toLowerCase();
    if (!q) return true;
    return (user?.email || '').toLowerCase().includes(q) || (user?.team || '').toLowerCase().includes(q);
}

// The date on its own is what the table has room for; the tooltip carries the time.
function stampDate(ts) {
    if (!ts) return null;
    return new Date(ts).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}
function stampFull(ts) {
    if (!ts) return '';
    return new Date(ts).toLocaleString();
}

// Deterministic avatar color from the email so each user keeps a stable hue.
function avatarColor(email) {
    const palette = ['#5C6BC0', '#26A69A', '#EF6C00', '#8E24AA', '#43A047', '#00838F', '#C62828', '#3949AB'];
    let h = 0;
    for (const c of (email || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
    return palette[h % palette.length];
}

function PermissionHeader({ label, info }) {
    return (
        <TableCell sx={{ width: '110px', textAlign: 'center', fontWeight: 700 }}>
            <Tooltip title={info}>
                <span style={{ cursor: 'help', borderBottom: '1px dotted #bbb' }}>{label}</span>
            </Tooltip>
        </TableCell>
    );
}

function Users() {
    const { org, accesstoken, me, onOrgUpdated } = useOutletContext();
    const [ users, setUsers ] = useState(JSON.parse(JSON.stringify(org.users)));
    const [ editing, setEditing ] = useState(-1);
    const [ saving, setSaving ] = useState(false);
    const [ fieldError, setFieldError ] = useState(false);
    const [ saveError, setSaveError ] = useState(false);
    const [ saveSuccess, setSaveSuccess ] = useState({show: false});
    const [ query, setQuery ] = useState('');
    const [ teamFilter, setTeamFilter ] = useState('all');
    const [ showDeleted, setShowDeleted ] = useState(false);

    const deletedUsers = _.orderBy(org.deletedUsers || [], ['deletedAt'], ['desc']);

    useEffect(() => {
        const $users = JSON.parse(JSON.stringify(org.users));
        $users.forEach(u => u.manages = (u.manages || []).includes(u.team) ? [u.team] : []);
        setUsers($users);
    }, [org.users])

    async function onFieldOk(email) {
        if (!_.trim(email)) {
            setFieldError(true)
        } if (!validator.validate(email)) {
            setFieldError(true)
        } else {
            setSaving(true);
            try {
                const {data: saved} = await axios.post(`/api/organizations/${org.id}/users?accesstoken=${accesstoken}`, users);
                // TODO: update org
                setSaveSuccess({...saveSuccess, show: true, message: 'Users saved successfully'});
                setSaveError(false);
                setSaving(false);
                setEditing(-1);
                onOrgUpdated({ ...org, users: saved });
            } catch {
                setSaveError(true);
                setSaveSuccess({ ...saveSuccess, show: false });
                setSaving(false);
            }
        }
    }

    async function onDelete(index) {
        setSaving(true);
        const removed = users[index];
        try {
            const {data: saved} = await axios.post(`/api/organizations/${org.id}/users?accesstoken=${accesstoken}`, _.filter(users, (u, i) => index !== i));
            // TODO: update org
            setSaveSuccess({...saveSuccess, show: true, message: 'Users saved successfully'});
            setSaveError(false);
            setSaving(false);
            setEditing(-1);
            // The server has written the removal down; mirror it here so the row moves
            // to the deleted list now instead of waiting for the next load, which is
            // when the server's own stamp — a moment earlier than this one — takes over.
            const stillHere = new Set((saved || []).map(u => u.email));
            const nextDeleted = (org.deletedUsers || []).filter(d => !stillHere.has(d.email));
            if (removed && removed.email && !stillHere.has(removed.email)) {
                nextDeleted.push({ ...removed, deletedAt: Date.now() });
            }
            onOrgUpdated({ ...org, users: saved, deletedUsers: nextDeleted });
        } catch {
            setSaveError(true);
            setSaveSuccess({ ...saveSuccess, show: false });
            setSaving(false);
        }
    }

    async function onResetPassword(index) {
        setSaving(true);
        const email = users[index].email;
        try {
            await axios.post(`/api/organizations/${org.id}/users/${email}/resetpassword?accesstoken=${accesstoken}`, {});
            setSaveSuccess({...saveSuccess, show: true, message: `Reset password email sent to ${email}`});
            setSaveError(false);
            setSaving(false);
        } catch {
            setSaveError(true);
            setSaveSuccess({ ...saveSuccess, show: false });
            setSaving(false);
        }
    }

    function onSnackbarClose() {
        setSaveError(false);
        setSaveSuccess({ ...saveSuccess, show: false });
    }

    function onAdd() {
        setUsers([{email: '', isAdmin: false, manages:[], team: me.isAdmin ? '' : me.manages[0] }, ...users]);
        setEditing(0);
    }

    const cellSx = { borderColor: 'divider' };

    return (
        <Box>
            <Header
                icon={faUsers}
                title={'Users'}
                buttons={[{
                    title: 'Add User',
                    icon: faUserPlus,
                    color: 'monada-button',
                    disabled: editing !== -1,
                    action: onAdd
                }]}
            />
            <Box sx={{ p: 3, flexGrow: 1 }}>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center' }}>
                    <TextField
                        size='small'
                        placeholder='Search users…'
                        value={query}
                        disabled={editing !== -1}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{ startAdornment: (
                            <InputAdornment position='start'>
                                <FontAwesomeIcon icon={faMagnifyingGlass} style={{ fontSize: 13, opacity: 0.5 }} />
                            </InputAdornment>
                        ) }}
                        sx={{ width: 260 }}
                    />
                    <TextField
                        size='small'
                        select
                        label='Team'
                        value={teamFilter}
                        disabled={editing !== -1}
                        onChange={(e) => setTeamFilter(e.target.value)}
                        sx={{ width: 180 }}
                    >
                        <MenuItem value='all'>All teams</MenuItem>
                        {distinctTeamNames(users).map(name => (
                            <MenuItem key={name} value={name.toLowerCase()}>{name}</MenuItem>
                        ))}
                    </TextField>
                    <FormControlLabel
                        control={
                            <Switch
                                size='small'
                                checked={showDeleted}
                                disabled={editing !== -1 || deletedUsers.length === 0}
                                onChange={(e) => setShowDeleted(e.target.checked)}
                            />
                        }
                        label={<Typography variant='body2'>Show deleted{deletedUsers.length ? ` (${deletedUsers.length})` : ''}</Typography>}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        {(users || []).length} user{(users || []).length === 1 ? '' : 's'}
                    </Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 240px)', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Table stickyHeader size='small'>
                        <TableHead>
                            <TableRow sx={{ '& th': { backgroundColor: 'grey.50', fontWeight: 700 } }}>
                                <TableCell>Email</TableCell>
                                <TableCell>TMS Unique ID</TableCell>
                                <TableCell>Team</TableCell>
                                <TableCell sx={{ width: '150px' }}>Added</TableCell>
                                <PermissionHeader label='Team Visibility' info='Can view the whole team’s opportunities (manages their team)' />
                                <PermissionHeader label='Company Visibility' info='Org admin — full company visibility and user management' />
                                <PermissionHeader label='Financials' info='Can see buy rates, costs, and profit figures' />
                                <TableCell sx={{ width: '240px' }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(users || []).map((user, index) => {
                                const teamName = ((user.team || '').split(':')[0] || '').trim().toLowerCase();
                                const visible = editing === index
                                    || (matchesQuery(user, query) && (teamFilter === 'all' || teamName === teamFilter));
                                if (!visible) return null;
                                const teamLabel = (user.team || '').split(':')[0];
                                const teamId = (user.team || '').split(':')[1];
                                return (
                                <TableRow key={index} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' }, '& td': cellSx }}>
                                    <TableCell>
                                        { editing === index ? (
                                            <FormControl variant='outlined' fullWidth>
                                                <OutlinedInput
                                                    placeholder='Email'
                                                    id='email'
                                                    size='small'
                                                    value={user.email}
                                                    disabled={saving}
                                                    onChange={(event) => {
                                                        const _users = JSON.parse(JSON.stringify(users));
                                                        _users[index].email = event.target.value;
                                                        setUsers(_users);
                                                        setFieldError(false)
                                                    }}
                                                    error={fieldError}
                                                />
                                            </FormControl>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: avatarColor(user.email) }}>
                                                    {initials(user.email)}
                                                </Avatar>
                                                <Typography variant='body2'>{user.email}</Typography>
                                                {user.email === me.email && (
                                                    <Chip label='You' size='small' variant='outlined' sx={{ height: 18, fontSize: 10 }} />
                                                )}
                                            </Box>
                                        ) }
                                    </TableCell>
                                    <TableCell>
                                        { editing === index ? (
                                            <FormControl variant='outlined' fullWidth>
                                                <OutlinedInput
                                                    placeholder='TMS Unique ID'
                                                    id='tmsUniqueId'
                                                    size='small'
                                                    value={user.tmsUniqueId || ''}
                                                    disabled={saving}
                                                    onChange={(event) => {
                                                        const _users = JSON.parse(JSON.stringify(users));
                                                        _users[index].tmsUniqueId = event.target.value;
                                                        setUsers(_users);
                                                    }}
                                                />
                                            </FormControl>
                                        ) : (user.tmsUniqueId
                                            ? <Typography variant='body2'>{user.tmsUniqueId}</Typography>
                                            : <Typography variant='body2' sx={{ color: 'text.disabled' }}>—</Typography>) }
                                    </TableCell>
                                    <TableCell>
                                        { editing === index ? (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <FormControl variant='outlined' fullWidth>
                                                    <OutlinedInput
                                                        placeholder='Team Name'
                                                        id='teamName'
                                                        size='small'
                                                        value={(user.team || '').split(':')[0]}
                                                        disabled={saving || !me.isAdmin}
                                                        onChange={(event) => {
                                                            const _users = JSON.parse(JSON.stringify(users));
                                                            _users[index].team = `${event.target.value}:${(user.team || '').split(':')[1] || ''}`;
                                                            setUsers(_users);
                                                            setFieldError(false)
                                                        }}
                                                        error={fieldError}
                                                    />
                                                </FormControl>
                                                <FormControl variant='outlined' fullWidth>
                                                    <OutlinedInput
                                                        placeholder='Team Unique ID'
                                                        id='teamId'
                                                        size='small'
                                                        value={(user.team || '').split(':')[1] || ''}
                                                        disabled={saving || !me.isAdmin}
                                                        onChange={(event) => {
                                                            const _users = JSON.parse(JSON.stringify(users));
                                                            _users[index].team = `${(user.team || '').split(':')[0] || ''}:${event.target.value}`;
                                                            setUsers(_users);
                                                            setFieldError(false)
                                                        }}
                                                        error={fieldError}
                                                    />
                                                </FormControl>
                                            </Box>
                                        ) : (teamLabel
                                            ? <Tooltip title={`Unique ID: ${teamId || '—'}`}>
                                                  <Chip label={teamLabel} size='small' color={teamId ? 'default' : 'warning'} variant={teamId ? 'filled' : 'outlined'} />
                                              </Tooltip>
                                            : <Typography variant='body2' sx={{ color: 'text.disabled' }}>—</Typography>) }
                                    </TableCell>
                                    <TableCell>
                                        { user.createdAt ? (
                                            <Tooltip title={stampFull(user.createdAt)}>
                                                <Typography variant='body2' sx={{ cursor: 'help' }}>{stampDate(user.createdAt)}</Typography>
                                            </Tooltip>
                                        ) : (
                                            // Users who were on the list before Monada started
                                            // recording this have no honest date to show.
                                            <Tooltip title='Added before Monada started recording this'>
                                                <Typography variant='body2' sx={{ color: 'text.disabled', cursor: 'help' }}>—</Typography>
                                            </Tooltip>
                                        ) }
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        {editing !== index && (user.manages && user.team && user.manages.includes(user.team)
                                            ? <FontAwesomeIcon icon={faCheck} style={{ color: '#43A047' }} />
                                            : <span style={{ color: '#ccc' }}>—</span>)}
                                        {editing === index && (
                                            <Switch size='small' checked={!!user.manages && !!user.team && user.manages.includes(user.team)} disabled={saving || !user.team} onClick={() => {
                                                const _users = JSON.parse(JSON.stringify(users));
                                                if ((_users[index].manages || []).includes(_users[index].team)) {
                                                    _users[index].manages = [];
                                                } else {
                                                    _users[index].manages = [_users[index].team];
                                                }
                                                setUsers(_users);
                                            }}/>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        {editing !== index && (user.isAdmin
                                            ? <FontAwesomeIcon icon={faCheck} style={{ color: '#43A047' }} />
                                            : <span style={{ color: '#ccc' }}>—</span>)}
                                        {editing === index && (
                                            <Switch size='small' checked={user.isAdmin} disabled={saving} onClick={() => {
                                                const _users = JSON.parse(JSON.stringify(users));
                                                _users[index].isAdmin = !_users[index].isAdmin;
                                                setUsers(_users);
                                            }}/>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        {editing !== index && (user.showFinancials
                                            ? <FontAwesomeIcon icon={faCheck} style={{ color: '#43A047' }} />
                                            : <span style={{ color: '#ccc' }}>—</span>)}
                                        {editing === index && (
                                            <Switch size='small' checked={!!user.showFinancials} disabled={saving} onClick={() => {
                                                const _users = JSON.parse(JSON.stringify(users));
                                                _users[index].showFinancials = !_users[index].showFinancials;
                                                setUsers(_users);
                                            }}/>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ width: '240px', textAlign: 'center' }}>
                                        { editing === index && saving && (
                                            <CircularProgress size={20}/>
                                        )}
                                        { editing === index && !saving ? (
                                            <div>
                                                <Box>
                                                    <Button
                                                        color='primary'
                                                        variant='contained'
                                                        onClick={() => onFieldOk(user.email)}
                                                    >
                                                        <FontAwesomeIcon icon={faCheck} />
                                                    </Button>
                                                    &nbsp;
                                                    <Button color='secondary' variant='contained' onClick={() => {
                                                        setUsers(JSON.parse(JSON.stringify(org.users)));
                                                        setEditing(-1);
                                                    }}>
                                                        <FontAwesomeIcon icon={faXmark} />
                                                    </Button>
                                                    &nbsp;
                                                    <Button disabled={users.length === 1 || index >= org.users.length} color='error' onClick={() => onDelete(index)}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </Button>
                                                </Box>
                                                <Box style={{ marginTop: '6px', width: '143px' }}>
                                                    <Button onClick={() => onResetPassword(index)} size='small'>
                                                        Reset Password
                                                    </Button>
                                                </Box>
                                            </div>
                                        ) : editing === -1 ? (
                                            <Button color='primary' onClick={() => setEditing(index)}>
                                                <FontAwesomeIcon icon={faPenToSquare} />
                                            </Button>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                            {showDeleted && deletedUsers.map((user, i) => {
                                const teamName = ((user.team || '').split(':')[0] || '').trim().toLowerCase();
                                if (!(matchesQuery(user, query) && (teamFilter === 'all' || teamName === teamFilter))) return null;
                                const teamLabel = (user.team || '').split(':')[0];
                                return (
                                <TableRow key={`deleted-${user.email}-${i}`} hover sx={{ opacity: 0.6, '& td': cellSx }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                            <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'grey.400' }}>
                                                {initials(user.email)}
                                            </Avatar>
                                            <Typography variant='body2' sx={{ textDecoration: 'line-through' }}>{user.email}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {user.tmsUniqueId
                                            ? <Typography variant='body2'>{user.tmsUniqueId}</Typography>
                                            : <Typography variant='body2' sx={{ color: 'text.disabled' }}>—</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        {teamLabel
                                            ? <Chip label={teamLabel} size='small' variant='outlined' />
                                            : <Typography variant='body2' sx={{ color: 'text.disabled' }}>—</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        { user.createdAt ? (
                                            <Tooltip title={stampFull(user.createdAt)}>
                                                <Typography variant='body2' sx={{ cursor: 'help' }}>{stampDate(user.createdAt)}</Typography>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant='body2' sx={{ color: 'text.disabled' }}>—</Typography>
                                        ) }
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        {user.manages && user.team && user.manages.includes(user.team)
                                            ? <FontAwesomeIcon icon={faCheck} style={{ color: '#43A047' }} />
                                            : <span style={{ color: '#ccc' }}>—</span>}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        {user.isAdmin
                                            ? <FontAwesomeIcon icon={faCheck} style={{ color: '#43A047' }} />
                                            : <span style={{ color: '#ccc' }}>—</span>}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        {user.showFinancials
                                            ? <FontAwesomeIcon icon={faCheck} style={{ color: '#43A047' }} />
                                            : <span style={{ color: '#ccc' }}>—</span>}
                                    </TableCell>
                                    <TableCell sx={{ width: '240px', textAlign: 'center' }}>
                                        <Tooltip title={stampFull(user.deletedAt)}>
                                            <Chip
                                                label={user.deletedAt ? `Deleted ${stampDate(user.deletedAt)}` : 'Deleted'}
                                                size='small'
                                                color='error'
                                                variant='outlined'
                                                sx={{ cursor: 'help' }}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Snackbar open={saveError} autoHideDuration={6000} onClose={onSnackbarClose}>
                    <Alert onClose={onSnackbarClose} severity='error' variant='filled' sx={{ width: '100%' }}>
                        Error saving. Please try again.
                    </Alert>
                </Snackbar>
                <Snackbar open={saveSuccess.show} autoHideDuration={6000} onClose={onSnackbarClose}>
                    <Alert onClose={onSnackbarClose} severity='success' variant='filled' sx={{ width: '100%' }}>
                        {saveSuccess.message}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    )
};

export default Users;
